import { useState, useEffect } from 'react';
import { useAuth } from "../../../contexts/AuthContext";
import { db } from "../../../firebase";
import { collection, doc, arrayUnion, getDoc, runTransaction } from "firebase/firestore";
import getServerTimestamp from '../../../functions/endpoints/GetServerTimestamp';
import { useNavigate, useParams } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ErrorDisplay from '../../../components/ErrorDisplay';
import Backdrop from "@mui/material/Backdrop";
import * as theme from '../../../theme';
import dayjs from 'dayjs';
import GeneralSelect from '../../../components/GeneralSelect';
import AvailableScanDates from '../../../components/schedule/AvailableScanDates';
import { scanList } from '../../../contexts/FocusScanContext';
import { miniScanList } from '../../../contexts/MiniScanContext';
import { fetchInitialScanDates, fetchMoreDates, handleDateSelect } from '../../../functions/scheduleUtils';
import { processScanPayment } from '../../../functions/endpoints/ProcessScanPayment';
import ConfirmationModal from '../../../components/ConfirmationModal';
import CostContext from '../../../contexts/CostContext';
import BillingCheckModal from '../../../components/BillingCheckModal';
import AddableList from '../../../components/AddableList';
import { Info } from '@mui/icons-material';
import FocusScansList from '../../../components/FocusScansList';
import MiniScansList from '../../../components/MiniScansList';
import { checkBillingCards } from '../../../functions/billingUtils';
import { Modal, Typography, FormControl, FormControlLabel, Radio, RadioGroup, Checkbox } from '@mui/material';
import { sendEmail } from "../../../functions/endpoints/SendEmail";

export default function AddScan() {
  const [formData, setFormData] = useState({
    scanDate: null,
    concern: [],
    supplements: [],
    firstFocusScan: '',
    secondFocusScan: '',
    firstMiniScan: '',
    secondMiniScan: '',
    multiHomeDiscount: false, // New field for multi-home discount flag
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [hoveredDateIndex, setHoveredDateIndex] = useState(null);
  const [lastFetchedDate, setLastFetchedDate] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cardSelectModalOpen, setCardSelectModalOpen] = useState(false);
  const [focusInfoModalOpen, setFocusInfoModalOpen] = useState(false);
  const [miniInfoModalOpen, setMiniInfoModalOpen] = useState(false);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [cardBrand, setCardBrand] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [availableCards, setAvailableCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [practitionerId, setPractitionerId] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [practitionerData, setPractitionerData] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id: clientId } = useParams();

  const clearError = () => {
    setError('');
  };

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const clientRef = doc(db, "clients", clientId);
        const clientSnap = await getDoc(clientRef);
        if (clientSnap.exists()) {
          const data = clientSnap.data();
          setPractitionerId(data.practitionerId);
          setClientData(data);
          if (data.practitionerId) {
            const pracRef = doc(db, "practitioners", data.practitionerId);
            const pracSnap = await getDoc(pracRef);
            if (pracSnap.exists()) {
              setPractitionerData(pracSnap.data());
            } else {
              setError("Practitioner not found.");
            }
          }
        } else {
          setError("Client not found.");
          navigate(`/clients/${clientId}`);
        }
      } catch (err) {
        setError("Error fetching client details: " + err.message);
        navigate(`/clients/${clientId}`);
      }
    };

    fetchClientData();
  }, [clientId, navigate]);

  useEffect(() => {
    if (clientData && practitionerId) {
      const isSelf = clientId === practitionerId;
      const isFreeEligible = isSelf && clientData.hasUsedFreeScan === false;
      setIsFree(isFreeEligible);
      if (!isFreeEligible) {
        // Fetch available cards
        checkBillingCards(practitionerId)
          .then(cards => {
            setAvailableCards(cards);
            if (cards.length === 1) {
              setCardId(cards[0].cardId);
              setCardBrand(cards[0].brand);
              setCardLast4(cards[0].last4);
              setSelectedCardId(cards[0].cardId);
            } else if (cards.length > 1) {
              setCardSelectModalOpen(true);
            } else {
              setBillingModalOpen(true);
            }
          })
          .catch(err => {
            setError(err.cause || err.message);
            setBillingModalOpen(true);
          });
      }
    }
  }, [clientData, practitionerId]);

  useEffect(() => {
    const minDate = dayjs().add(12, 'day').startOf('day');
    fetchInitialScanDates(minDate, setAvailableDates, setLastFetchedDate, setError);
  }, []);

  const addItem = (field, value) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], value.trim()]
    });
  };

  const removeItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const handleCheckboxChange = (event) => {
    setFormData({
      ...formData,
      multiHomeDiscount: event.target.checked,
    });
  };

  const handleCardSelection = () => {
    const selectedCard = availableCards.find(card => card.cardId === selectedCardId);
    if (selectedCard) {
      setCardId(selectedCard.cardId);
      setCardBrand(selectedCard.brand);
      setCardLast4(selectedCard.last4);
      setCardSelectModalOpen(false);
    } else {
      setError("Please select a card.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.scanDate) {
      setError("Please select a valid scan date.");
      return;
    }
    if (!isFree && !cardId) {
      setError("Please select a payment card.");
      return;
    }
    setModalOpen(true);
  };

  const handleConfirm = async () => {
    setModalOpen(false);
    setLoading(true);
    setError("");

    try {
      const scanId = doc(collection(db, `clients/${clientId}/scans`)).id;

      let transactionId;
      let card = null;
      let amount = CostContext.ScanDownAmount;
      let transType = "scan-down";

      if (!isFree) {
        const practitionerRef = doc(db, "practitioners", practitionerId);
        const practitionerSnap = await getDoc(practitionerRef);
        if (!practitionerSnap.exists() || !practitionerSnap.data().squareId) {
          throw new Error("Cannot process payment, missing customer record in payment system.");
        }
        const squareId = practitionerSnap.data().squareId;

        const paymentResult = await processScanPayment({
          squareId,
          type: "scan-down",
          cardId,
          amount
        });
        transactionId = paymentResult.transactionId;
        card = paymentResult.card;
      } else {
        transactionId = 'free-onboarding-scan';
        amount = 0;
        transType = "free-scan-down";
      }

      const timestamp = await getServerTimestamp();

      await runTransaction(db, async (transaction) => {
        const scanRef = doc(db, `clients/${clientId}/scans`, scanId);
        const scanData = {
          ...formData,
          dateCreated: timestamp,
          dateModified: timestamp,
          scanDate: formData.scanDate ? formData.scanDate.format('YYYY-MM-DD') : null,
          kitPaid: {
            datePaid: timestamp,
            transId: transactionId,
            userId: currentUser.uid
          },
          transactions: [{
            transId: transactionId,
            amount,
            date: timestamp,
            type: 'scan-down'
          }],
          comments: [],
          cardId: isFree ? null : cardId, // Store the selected cardId
          multiHomeDiscount: formData.multiHomeDiscount, // Save the multi-home discount flag
        };
        if (isFree) {
          scanData.isFreeScan = true;
        }
        transaction.set(scanRef, scanData);

        const clientRef = doc(db, "clients", clientId);
        const newScanSummary = {
          id: scanId,
          scanDate: scanData.scanDate,
          isComplete: false,
        };
        const clientUpdates = {
          scans: arrayUnion(newScanSummary),
        };
        if (isFree) {
          clientUpdates.hasUsedFreeScan = true;
        }
        transaction.update(clientRef, clientUpdates);

        const monthKey = formData.scanDate.format('YYYYMM');
        const dayKey = formData.scanDate.format('DD');
        const scheduleDocRef = doc(db, 'schedule', monthKey);
        const scheduleDocSnap = await getDoc(scheduleDocRef);
        const monthData = scheduleDocSnap.exists()
          ? scheduleDocSnap.data()
          : { month: `${monthKey.slice(0, 4)}-${monthKey.slice(4)}`, days: {} };
        const dayData = monthData.days[dayKey] || {
          date: formData.scanDate.format('YYYY-MM-DD'),
          practitioners: [],
          scheduled: [],
          scansBooked: 0,
          totalScans: 0,
        };
        const updatedDayData = {
          ...dayData,
          scheduled: [
            ...dayData.scheduled,
            {
              scanId,
              clientId,
              practitionerId,
              timestamp: timestamp,
            },
          ],
          scansBooked: dayData.scansBooked + 1,
        };
        const updatedMonthData = {
          ...monthData,
          days: {
            ...monthData.days,
            [dayKey]: updatedDayData,
          },
        };
        transaction.set(scheduleDocRef, updatedMonthData, { merge: true });

        if (!isFree) {
          const currentYear = dayjs().year().toString();
          const practitionerTransRef = doc(db, `practitioners/${practitionerId}/transactions`, currentYear);
          transaction.set(practitionerTransRef, {
            trans: arrayUnion({
              id: transactionId,
              type: transType,
              dateCreated: timestamp,
              scanId,
              clientId,
              card,
              amount
            }),
          }, { merge: true });
        }

        const activeScansRef = doc(db, "app", "activeScans");
        const newActiveScan = {
          id: scanId,
          date: scanData.scanDate,
          clientId,
          practitionerId,
          scanStatus: 1,
          commentsToRead: false,
          modifiedDate: timestamp
        };
        transaction.set(activeScansRef, {
          scans: arrayUnion(newActiveScan)
        }, { merge: true });

        const practitionerScansRef = doc(db, "activeScans", practitionerId);
        const newPractitionerScan = {
          id: scanId,
          date: scanData.scanDate,
          clientId,
          scanStatus: 1,
          commentsToRead: false,
          modifiedDate: timestamp
        };
        transaction.set(practitionerScansRef, {
          scans: arrayUnion(newPractitionerScan)
        }, { merge: true });
      });

      // Send Email
      if (!practitionerData) {
        throw new Error("Practitioner data not available.");
      }
      const practitionerName = `${practitionerData.firstName} ${practitionerData.lastName}`;
      const clientName = `${clientData.firstName} ${clientData.lastName}`;
      const formattedScanDate = formData.scanDate.format('dddd, MMMM D, YYYY');
      const emailData = {
        recipientEmail: process.env.REACT_APP_ENVIRONMENT === 'PROD' ? 'hello@trubalancebioenergetics.com' : "jhatlestad@gmail.com",
        recipientName: "TruBalance Admin",
        subject: "New Scan Scheduled by Practitioner",
        templateId: "scan-scheduled",
        dynamicTemplateData: {
          practitionerName,
          clientName,
          scanDate: formattedScanDate
        },
        emailType: "scan_scheduled",
        contextData: {
          scanId,
          clientId,
          practitionerId
        },
        userId: currentUser.uid,
        isTest: false
      };

      sendEmail(emailData);

      navigate(`/clients/${clientId}/scans/${scanId}`);
    } catch (err) {
      let errorMessage = err.message;
      if (err.message.includes("NO_CARD_ON_FILE")) {
        errorMessage = "Cannot process payment, we do not have a card on file to charge.";
      } else if (err.message.includes("CARD_DECLINED")) {
        errorMessage = "Cannot process payment, card declined due to insufficient funds or other issue.";
      } else if (err.message.includes("SERVICE_UNAVAILABLE")) {
        errorMessage = "Payment system is currently unavailable. Please try again later.";
      } else if (err.message.includes("Failed to save")) {
        errorMessage = "Failed to save scan details. Please try again.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCardValidated = (billingInfo) => {
    setCardId(billingInfo.cardId);
    setCardBrand(billingInfo.brand);
    setCardLast4(billingInfo.last4);
    setSelectedCardId(billingInfo.cardId);
    setAvailableCards([{ cardId: billingInfo.cardId, brand: billingInfo.brand, last4: billingInfo.last4, type: billingInfo.type || "business" }]);
    setBillingModalOpen(false);
  };

  const today = dayjs();
  const maxDate = today.add(3, 'month').endOf('day');
  const showMoreButtonVisible = lastFetchedDate && lastFetchedDate.isBefore(maxDate);

  const modalTitle = isFree ? "Confirm Schedule" : "Confirm Payment";
  const modalDescription = isFree 
    ? "This is your free onboarding scan. No payment will be required. Confirm to schedule."
    : `We will charge the following card on file in the amount of $${CostContext.ScanDown} to reserve this date. Please confirm.`;
  const modalSecondLine = isFree ? null : `${cardBrand} x${cardLast4}`;
  const modalConfirmText = isFree ? "Confirm" : "Confirm Payment";

  return (
    <div style={theme.Container.basic}>
      <div style={theme.Heading}>SCHEDULE SCAN</div>
      <div style={theme.SubHeadingText}>
        Please select a scan date at least two weeks from today to allow sufficient time for mailing the kit to your client and for them to return it. The kit must be mailed back at least five days before the scheduled scan date.
      </div>
      <div style={{ marginTop: 15, marginBottom: 30, position: 'relative' }}>
        <Box sx={{ marginBottom: 2 }}>
          <ErrorDisplay
            message={error}
            type={'error'}
            clearMessage={clearError}
          />
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ ...theme.FullForm.form, gap: 4 }}
        >
          <AvailableScanDates
            availableDates={availableDates}
            selectedDateIndex={selectedDateIndex}
            hoveredDateIndex={hoveredDateIndex}
            setHoveredDateIndex={setHoveredDateIndex}
            handleDateSelect={(index) => handleDateSelect(index, availableDates, setSelectedDateIndex, setFormData)}
            showMoreButtonVisible={showMoreButtonVisible}
            fetchMoreDates={() => fetchMoreDates(lastFetchedDate, setAvailableDates, setLastFetchedDate, setError)}
            disabled={loading}
          />
          <Box sx={{ ...theme.FullForm.form, gap: 2, marginBottom: 2 }}>
            <div style={{ paddingLeft: 6 }}>List areas of concern for this scan:</div>
            <AddableList
              label="New Concern"
              items={formData.concern}
              onAdd={(value) => addItem('concern', value)}
              onRemove={(index) => removeItem('concern', index)}
              disabled={loading}
              isObject={false}
            />
          </Box>
          <Box sx={{ ...theme.FullForm.form, gap: 2, marginBottom: 4 }}>
            <div style={{ paddingLeft: 6 }}>List specific supplements to scan for (max 5):</div>
            <AddableList
              label="New Supplement"
              items={formData.supplements}
              onAdd={(value) => addItem('supplements', value)}
              onRemove={(index) => removeItem('supplements', index)}
              disabled={loading}
              isObject={false}
              maxItems={5}
            />
          </Box>
          <div>
            <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
              <GeneralSelect
                value={formData.firstFocusScan}
                onChange={(e) => setFormData({ ...formData, firstFocusScan: e.target.value })}
                items={scanList}
                name="firstFocusScan"
                label="Focus Scan Request 1"
                sx={{ flex: 1 }}
                disabled={loading}
              />
              <GeneralSelect
                value={formData.secondFocusScan}
                onChange={(e) => setFormData({ ...formData, secondFocusScan: e.target.value })}
                items={scanList}
                name="secondFocusScan"
                label="Focus Scan Request 2"
                sx={{ flex: 1 }}
                disabled={loading}
              />
            </Box>
            <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
              <Button startIcon={<Info sx={{ fontSize: 16 }} />} onClick={() => setFocusInfoModalOpen(true)}>
                Focus Scan Request Info
              </Button>
            </div>
          </div>
          <div>
            <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
              <GeneralSelect
                value={formData.firstMiniScan}
                onChange={(e) => setFormData({ ...formData, firstMiniScan: e.target.value })}
                items={miniScanList}
                name="firstMiniScan"
                label="Mini Scan Request 1"
                sx={{ flex: 1 }}
                disabled={loading}
              />
              <GeneralSelect
                value={formData.secondMiniScan}
                onChange={(e) => setFormData({ ...formData, secondMiniScan: e.target.value })}
                items={miniScanList}
                name="secondMiniScan"
                label="Mini Scan Request 2"
                sx={{ flex: 1 }}
                disabled={loading}
              />
            </Box>
            <div style={{ fontSize: 12, color: theme.Colors.GrayMedium, marginLeft: 4, marginTop: 8 }}>
              Mini scans are not required. If any show up in the Root Cause scan, we will scan those regardless. This is just if you specifically want us to include this in the Root Cause scan. These will not be a separate report; they will be included in the Root Cause report.
            </div>
            <div style={{ fontSize: 14, display: 'flex', alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
              <Button startIcon={<Info sx={{ fontSize: 16 }} />} onClick={() => setMiniInfoModalOpen(true)}>
                Mini Scan Info
              </Button>
            </div>
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.multiHomeDiscount}
                onChange={handleCheckboxChange}
                name="multiHomeDiscount"
                color="primary"
                disabled={loading}
              />
            }
            label="Are you scheduling multiple scans for this household (they may be eligible for a discount)?"
          />
          <div style={theme.SubHeadingText}>
            A ${CostContext.ScanDown} deposit will be charged upon booking. Cancel before the kit is mailed for a full refund. After mailing, you can reschedule or cancel, but the deposit will not be refunded. The balance will be charged on the scan date.
          </div>
          <Box sx={{ display: "flex", width: '100%', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={() => navigate(`/clients/${clientId}`)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || (!isFree && !cardId)}
              sx={{ flex: 3, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} color="inherit" />
                  Processing...
                </>
              ) : (
                "Schedule Scan"
              )}
            </Button>
          </Box>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
      <ConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        title={modalTitle}
        description={modalDescription}
        secondLine={modalSecondLine}
        confirmButtonText={modalConfirmText}
        error={error}
      />
      <ConfirmationModal
        open={focusInfoModalOpen}
        onClose={() => setFocusInfoModalOpen(false)}
        onConfirm={() => setFocusInfoModalOpen(false)}
        title="Focus Scan Request Information"
        description="Below is a list of available focus scans and their descriptions."
        confirmButtonText="Close"
        hideCancelButton={true}
        children={<FocusScansList />}
      />
      <ConfirmationModal
        open={miniInfoModalOpen}
        onClose={() => setMiniInfoModalOpen(false)}
        onConfirm={() => setMiniInfoModalOpen(false)}
        title="Mini Scan Request Information"
        description="Below is a list of available mini scans and their descriptions."
        confirmButtonText="Close"
        hideCancelButton={true}
        children={<MiniScansList />}
      />
      <BillingCheckModal
        practitionerId={practitionerId}
        open={billingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        onCardValidated={handleCardValidated}
      />
      <Modal
        open={cardSelectModalOpen}
        onClose={() => setCardSelectModalOpen(false)}
        aria-labelledby="card-select-modal-title"
        aria-describedby="card-select-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          outline: 'none',
        }}>
          <Typography id="card-select-modal-title" variant="h6" gutterBottom>
            Select Payment Card
          </Typography>
          <Typography id="card-select-modal-description" variant="body1" sx={{ mb: 2 }}>
            Please select the card to use for this scan’s downpayment. This card will also be used for the final payment.
          </Typography>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedCardId}
              onChange={(e) => setSelectedCardId(e.target.value)}
            >
              {availableCards.map((card) => (
                <FormControlLabel
                  key={card.cardId}
                  value={card.cardId}
                  control={<Radio />}
                  label={`${card.type.charAt(0).toUpperCase() + card.type.slice(1)} Card: ${card.brand} ending in ${card.last4}`}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setCardSelectModalOpen(false)}
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCardSelection}
              disabled={!selectedCardId}
            >
              Select Card
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}